import React from "react";
import {
  ShimmerDiv as BaseShimmerDiv,
  ShimmerTitle as BaseShimmerTitle,
  ShimmerCategoryItems as BaseShimmerCategoryItems,
} from "shimmer-effects-react";
import { useShimmerMode, useShimmerThemeProps } from "./useShimmerMode";

const CSS_SIZE_PATTERN = /%|px|rem|em|vw|vh|ch$/i;

const isCssSize = (value) =>
  typeof value === "string" && CSS_SIZE_PATTERN.test(value.trim());

const isCircleShimmer = (width, height, rounded) =>
  typeof width === "number" &&
  typeof height === "number" &&
  width === height &&
  typeof rounded === "number" &&
  rounded > 1;

/** shimmer-effects-react uses `rounded` in em; values > 1 are treated as px on a wrapper. */
const getWrapperBorderRadius = (rounded, width, height) => {
  if (typeof rounded !== "number") return undefined;
  if (isCircleShimmer(width, height, rounded)) return "50%";
  if (rounded > 1) return `${rounded}px`;
  return undefined;
};

const toLibraryRounded = (rounded, width, height) => {
  if (rounded == null) return 0.1;
  if (typeof rounded !== "number") return rounded;
  if (isCircleShimmer(width, height, rounded) || rounded > 1) return 0.1;
  return rounded;
};

const needsSizingWrapper = (width, height, rounded) =>
  isCssSize(width) ||
  isCssSize(height) ||
  getWrapperBorderRadius(rounded, width, height) != null;

export function ShimmerDiv({
  mode,
  width,
  height,
  rounded,
  className = "",
  style,
  ...props
}) {
  const themeMode = useShimmerMode();
  const resolvedMode = mode ?? themeMode;
  const themeProps = useShimmerThemeProps(resolvedMode);
  const libraryRounded = toLibraryRounded(rounded, width, height);

  if (!needsSizingWrapper(width, height, rounded)) {
    return (
      <BaseShimmerDiv
        {...props}
        {...themeProps}
        width={width}
        height={height}
        rounded={libraryRounded}
        className={className}
        style={style}
      />
    );
  }

  const wrapperRadius = getWrapperBorderRadius(rounded, width, height);
  const wrapperStyle = {
    width: isCssSize(width) ? width : typeof width === "number" ? width : undefined,
    height: isCssSize(height) ? height : typeof height === "number" ? height : undefined,
    borderRadius: wrapperRadius,
    overflow: "hidden",
    flex: width === "100%" ? "1 1 auto" : undefined,
    minWidth: width === "100%" ? 0 : undefined,
    ...style,
  };

  return (
    <div
      className={`skeleton-shimmer-slot ${className}`.trim()}
      style={wrapperStyle}
    >
      <BaseShimmerDiv
        {...props}
        {...themeProps}
        width="100%"
        height="100%"
        rounded={libraryRounded}
        className="shimmer-div"
      />
    </div>
  );
}

const withThemeMode = (Component) =>
  function ThemedShimmerComponent({ mode, from, via, to, ...props }) {
    const themeProps = useShimmerThemeProps(mode);
    return (
      <Component
        {...props}
        {...themeProps}
        from={from ?? themeProps.from}
        via={via ?? themeProps.via}
        to={to ?? themeProps.to}
      />
    );
  };

export const ShimmerTitle = withThemeMode(BaseShimmerTitle);
export const ShimmerCategoryItems = withThemeMode(BaseShimmerCategoryItems);
